// angular
import { Injectable } from "@angular/core";
// multi-asset
import * as CubiqTS from "shared/cubiq-model-multi-asset";
// global-services
import { BfxRBACService } from "app/bfx-common/services/bfx-rbac.service";
import { BfxUserProfileService } from "app/bfx-common/services/bfx-user-profile.service";

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

// cubiq-services
import {
  TraderGroupPromiseLookupService,
  ActiveUser,
  SystemConfigPromiseService,
  Roles,
  ActiveUserProfilePromiseService,
} from "shared/cubiq-services";
///

@Injectable()
export class OEPermissionsService {
  public permissions = {
    enterNewOrder: true,
    enterMarkupLimit: true,
    enterMarkupAlgo: true,
    enterMarkupBMark: true,
    enterTraderGroupLimit: true,
    enterTraderGroupAlgo: true,
    enterTraderGroupBenchmark: true,
    enterFixingDate: true,
  };

  /**********************************************************************/
  /* CONSTRUCTOR  */
  constructor(
    private rbac: BfxRBACService,
    private userProfile: BfxUserProfileService,
    private settingsLookup: SystemConfigPromiseService,
    private activeUserLookup: ActiveUserProfilePromiseService,
    private traderGroupService: TraderGroupPromiseLookupService,
  ) {
    this.initialize();
  }

  /**********************************************************************/
  /* INITIALIZATION */

  private initialize(): void {
    this.initializePermissions();
  }

  public initializePermissions() {
    this.settingsLookup.isPrimed().then(() => {
      this.activeUserLookup.getActiveUserData().then((user: ActiveUser) => {
        // permissions
        Promise.all([
          this.settingsLookup.hasRole(Roles.ORDER_ENTRY),
          this.settingsLookup.hasRole(Roles.ORDER_ENTRY_MARKUP_LIMIT),
          this.settingsLookup.hasRole(Roles.ORDER_ENTRY_MARKUP_ALGO),
          this.settingsLookup.hasRole(Roles.ROLE_ORDER_MODIFY_TRADER_GROUP_LIMIT),
          this.settingsLookup.hasRole(Roles.ROLE_ORDER_MODIFY_TRADER_GROUP_ALGO),
          this.settingsLookup.hasRole(Roles.ROLE_ORDER_MODIFY_TRADER_GROUP_BENCHMARK),
          this.settingsLookup.hasRole(Roles.ORDER_REQUEST_NDF_ODD_FIXING),
          this.settingsLookup.hasRole(Roles.ORDER_ENTRY_MARKUP_BENCHMARK),
        ]).then((values: Array<any>) => {
          this.permissions.enterNewOrder = values[0];
          this.permissions.enterMarkupLimit = values[1];
          this.permissions.enterMarkupAlgo = values[2];
          this.permissions.enterTraderGroupLimit = values[3];
          this.permissions.enterTraderGroupAlgo = values[4];
          this.permissions.enterTraderGroupBenchmark = values[5];
          this.permissions.enterFixingDate = values[6];
          this.permissions.enterMarkupBMark = values[7];
        });
      });
    });
  }

  public getSysConfigPermissions(): any {
    return this.permissions;
  }

  /**********************************************************************/
  /* METHODS  */
  public determineDIInterventionPermissions(
    operation: CubiqTS.InterventionOperationType,
    salesGroupCode: string,
    lockUserId: string,
  ): Promise<boolean> {
    return new Promise<boolean>((res, rej) => {
      this.rbac.hasRole("ROLE_DI_SALES_UNRESTRICTED").then((result: boolean) => {
        if (result) {
          res(result); // true
        } else if (salesGroupCode != null) {
          //this._salesGroupService.getSalesGroup(salesGroupCode).then((salesGroup: CubiqTS.SalesGroupImpl)=>{
          this.userProfile.getUserData().then((userData: ActiveUser) => {
            let salesGroupMatch: Array<string> = userData.salesGroupsIds.filter((item: string) => {
              return item === salesGroupCode;
            });

            if (salesGroupMatch != null && salesGroupMatch.length > 0) {
              // If active user belongs to TraderGroup
              if (operation === CubiqTS.InterventionOperationType.APPROVE) {
                // Approve permission
                this.rbac.hasRole("ROLE_DI_SALES_APPROVE").then((result2: boolean) => {
                  res(result2);
                });
              } else if (operation === CubiqTS.InterventionOperationType.REJECT) {
                // if asking for Reject
                this.rbac.hasRole("ROLE_DI_SALES_REJECT").then((result2: boolean) => {
                  res(result2);
                });
              } else if (operation === CubiqTS.InterventionOperationType.CLEAR) {
                // if asking for Clear
                this.rbac.hasRole(Roles.DI_CLEAR_FILL).then((result2: boolean) => {
                  res(result2);
                });
              } else {
                logger.out.error(
                  `ERROR: _oeWorker.determineDIInterventionPermissions(): Unknown operation: ${operation}`,
                );
                res(false);
              }
            } else {
              res(false);
            }
          });
          //	});
        } else {
          logger.out.error(
            `ERROR: _oeWorker.determineDIInterventionPermissions(): Sales group is null!`,
          );
          res(false);
        }
      });
    });
  }

  public determineAcceptanceNeededPermissions(
    operation: CubiqTS.AcceptanceAction,
    traderGroupCodes: Array<string> | string,
    srvOrderStates: Array<CubiqTS.OrderState> | CubiqTS.OrderState,
  ): Promise<boolean> {
    return new Promise<boolean>((res, rej) => {
      if (typeof traderGroupCodes === "string" && Array.isArray(srvOrderStates) === false) {
        // limit orders
        this.processAcceptanceNeededPermissions(
          operation,
          traderGroupCodes,
          srvOrderStates as CubiqTS.OrderState,
        ).then((result) => res(result));
      } else if (
        Array.isArray(traderGroupCodes) &&
        Array.isArray(srvOrderStates) &&
        traderGroupCodes.length > 0 &&
        traderGroupCodes.length === srvOrderStates.length
      ) {
        //bmarks
        let promiseArray = [];
        let result: boolean = false;
        for (let [i, traderGroupCode] of traderGroupCodes.entries()) {
          let srvOrderState = srvOrderStates[i];
          promiseArray.push(
            this.processAcceptanceNeededPermissions(operation, traderGroupCode, srvOrderState),
          );
        }

        Promise.all(promiseArray).then((values: Array<boolean>) => {
          if (Array.isArray(values) && values.length > 0) {
            for (let value of values) {
              result = result || value;
            }
            res(result);
          } else {
            res(false);
          }
        });
      } else {
        logger.out.error(
          `oeperm-s - determineAcceptanceNeededPermissions has invalid traderGroupCodes: ${traderGroupCodes}`,
        );
        res(false);
      }
    });
  }

  private processAcceptanceNeededPermissions(
    operation: CubiqTS.AcceptanceAction,
    traderGroupCode: string,
    orderState: CubiqTS.OrderState,
  ): Promise<boolean> {
    return new Promise<boolean>((res, rej) => {
      this.traderGroupService
        .getTraderGroupForSid(traderGroupCode)
        .then((traderGroup: CubiqTS.TradingGroupImpl) => {
          if (traderGroup != null) {
            if (
              traderGroup.isExternallyFilledFg &&
              (CubiqTS.OrderState.EXT_ACCEPTANCE_NEEDED === orderState ||
                CubiqTS.OrderState.EXT_CANCEL_ACCEPTANCE_NEEDED === orderState ||
                CubiqTS.OrderState.EXT_MODIFY_ACCEPTANCE_NEEDED === orderState)
            ) {
              // if TraderGroup is external and state is external?
              if (operation === CubiqTS.AcceptanceAction.ABORT) res(false);

              this.rbac.hasRole("ROLE_DI_TRADER_UNRESTRICTED").then((result: boolean) => {
                // permit force accept/reject
                res(result);
              });
            } else if (traderGroup.groupId.equals(-1)) {
              // if Default trader group?
              if (operation === CubiqTS.AcceptanceAction.ACCEPT) {
                res(false); // Accept is NOT allowed
              } else {
                res(true); // Reject and Abort are allowed
              }
            } else {
              // // TraderGroup is internal
              if (operation === CubiqTS.AcceptanceAction.ABORT) {
                // if asking for Abort
                res(true); // Abort Allowed
              } else {
                this.rbac.hasRole("ROLE_DI_TRADER_UNRESTRICTED").then((result: boolean) => {
                  if (result) {
                    // if active user has unrestricted permission?
                    res(true); // Accept/Reject/Abort ARE allowed
                  } else {
                    this.userProfile.getUserData().then((userData: ActiveUser) => {
                      // old
                      //let tradeGroupMatch: Array<string> = userData.tradingGroupsNames.filter((item: string)=>{ return item === traderGroupCode; });
                      // new
                      let tradeGroupMatch: Array<string> = userData.tradingGroupsIds.filter(
                        (item: string) => {
                          return item === traderGroupCode;
                        },
                      );

                      if (tradeGroupMatch != null && tradeGroupMatch.length > 0) {
                        // If active user belongs to TraderGroup
                        if (operation === CubiqTS.AcceptanceAction.ACCEPT) {
                          // if asking for Accept
                          this.rbac.hasRole("ROLE_DI_TRADER_ACCEPT").then((result2: boolean) => {
                            // check Accept permission
                            res(result2);
                          });
                        } else if (operation === CubiqTS.AcceptanceAction.REJECT) {
                          // if asking for Reject
                          this.rbac.hasRole("ROLE_DI_TRADER_REJECT").then((result2: boolean) => {
                            // check Reject permission
                            res(result2);
                          });
                        } else {
                          logger.out.error(
                            `oeperm-s - determineAcceptanceNeededPermissions(): Unknown operation '${operation}'`,
                          );
                          res(false);
                        }
                      } else {
                        res(false);
                      }
                    });
                  }
                });
              }
            }
          } else {
            res(false);
          }
        });
    });
  }

  // NB Visibiluty profile means we would never have seen the order if we could not see the client
  public determineIfUserCanWorkWithClient(activeUser: ActiveUser, clientId: string): boolean {
    if (activeUser.entryUnRestricted) return true;

    if (activeUser != null && activeUser.userTypeId === CubiqTS.UserType.SALES) {
      return activeUser.clientIdsInSalesGroupSet.has(clientId);
    }

    if (
      activeUser != null &&
      activeUser.clientIds != null &&
      activeUser.clientIds instanceof Array &&
      activeUser.clientIds.length > 0
    ) {
      return (
        activeUser.clientIds.find((item: string) => {
          return item === clientId;
        }) != null
      );
    }
    return false;
  }
}
